import type { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
  useRouterState,
} from "@tanstack/react-router";
import { GoogleAnalytics } from "tanstack-router-ga4";

import { NotFound } from "@/components/NotFound";
import { detectLocaleScript } from "@/i18n/detect";
import { isLocale, localeMeta } from "@/i18n/locales";
import { GA_MEASUREMENT_ID } from "@/lib/site";
import appCss from "@/styles/app.css?url";

const FONT_DM_SANS =
  "https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&display=swap";

const asyncCssScript = `(function(){function a(h){var l=document.createElement("link");l.rel="stylesheet";l.href=h;document.head.appendChild(l)}a(${JSON.stringify(appCss)});a(${JSON.stringify(FONT_DM_SANS)})})();`;

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()(
  {
    head: () => ({
      meta: [
        { charSet: "utf-8" },
        { name: "viewport", content: "width=device-width, initial-scale=1" },
      ],
      links: [
        {
          rel: "preload",
          href: "/images/phone.webp",
          as: "image",
          type: "image/webp",
        },
        {
          rel: "icon",
          href: "/icon-ui.webp",
          type: "image/webp",
        },
        { rel: "apple-touch-icon", href: "/icon.png" },
        { rel: "dns-prefetch", href: "https://www.googletagmanager.com" },
      ],
    }),
    notFoundComponent: NotFound,
    component: RootDocument,
  },
);

function RootDocument() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const maybeLocale = pathname.split("/").filter(Boolean)[0] ?? "";
  const lang = isLocale(maybeLocale) ? localeMeta[maybeLocale].html : "nl";

  return (
    <html lang={lang}>
      <head>
        {/* biome-ignore lint/security/noDangerouslySetInnerHtml: blocking locale redirect before first paint */}
        <script dangerouslySetInnerHTML={{ __html: detectLocaleScript }} />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preload" href={appCss} as="style" />
        <link rel="preload" href={FONT_DM_SANS} as="style" />
        {/* biome-ignore lint/security/noDangerouslySetInnerHtml: async CSS without render-blocking link tags */}
        <script dangerouslySetInnerHTML={{ __html: asyncCssScript }} />
        <noscript>
          <link rel="stylesheet" href={appCss} />
          <link rel="stylesheet" href={FONT_DM_SANS} />
        </noscript>
        <HeadContent />
      </head>
      <body>
        <GoogleAnalytics measurementId={GA_MEASUREMENT_ID} />
        <Outlet />
        <Scripts />
      </body>
    </html>
  );
}
